import { BOTTOMBAR } from '@components/v2/layout/AppTemplate/appTemplate.consts';
import IconButton from '@components/v2/ui/IconButton/IconButton';
import Link from '@components/v2/ui/Link/Link';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box, Grow } from '@mui/material';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import LangEnum from '../../../lang/LangEnum';
import { AppVersionTestIds } from '../../../utils/testIds/appVersion.testIds';
import { useTranslation } from '../../translation/TranslationContext';
import { useNewAppVersionPopper } from './hooks/useNewAppVersionPopper';

const NewAppVersionPopper = () => {
  const { translate, formatMessage } = useTranslation();
  const { isOpen, reloadApp, dismiss } = useNewAppVersionPopper();

  const renderReloadLink = (chunks: ReactNode) => (
    <Link
      component="button"
      color="text.primary"
      underline="always"
      testId={AppVersionTestIds.NEW_VERSION_RELOAD}
      sx={{ verticalAlign: 'baseline' }}
      onClick={reloadApp}
    >
      {chunks}
    </Link>
  );

  return (
    <Grow in={isOpen} style={{ transformOrigin: 'bottom left' }} unmountOnExit>
      <Box
        role="status"
        test-id={AppVersionTestIds.NEW_VERSION_POPPER}
        sx={(theme) => ({
          position: 'fixed',
          left: theme.spacing(1),
          bottom: BOTTOMBAR.height,
          zIndex: theme.zIndex.snackbar,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
          maxWidth: `calc(100vw - ${theme.spacing(2)})`,
          padding: theme.spacing(1, 1, 1, 1.5),
          boxSizing: 'border-box',
          borderRadius: `${theme.shape.borderRadiusLg}px`,
          border: `1px solid ${theme.palette.custom.border.secondary}`,
          backgroundColor: theme.palette.background.paper,
          boxShadow: theme.shadows[4],
          [theme.breakpoints.down('sm')]: {
            bottom: theme.spacing(1),
          },
        })}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.125, minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary">
            {translate(LangEnum.NEW_APP_VERSION_TITLE)}
          </Typography>
          <Typography variant="body2" color="text.primary">
            {formatMessage({ id: LangEnum.NEW_APP_VERSION_MESSAGE }, { link: renderReloadLink })}
          </Typography>
        </Box>

        <IconButton
          icon={X}
          size="small"
          shape="round"
          ariaLabel={translate(LangEnum.GLOBAL_CLOSE)}
          testId={AppVersionTestIds.NEW_VERSION_CLOSE}
          sx={{ margin: -0.5, '&:hover': { backgroundColor: 'transparent' } }}
          onClick={dismiss}
        />
      </Box>
    </Grow>
  );
};

export default NewAppVersionPopper;
